/**
 * StudioChatbotPage — 고객센터 > 챗봇상담 (로그인 회원 전용)
 *
 * 참고: 고객지원_상담체계_화면기획_V1.6.html 좌측 메뉴 "회원(고객센터) > 문의하기(챗봇)".
 * FaqPage·NoticesPage·InquiryPage와 같은 "고객센터" 섹션이라 같은 톤(견본 var(--gt-primary) 계열
 * inline style + Tailwind text-text/text-text-muted 혼용)을 그대로 따른다 — D-08 §6.4, Contents
 * 영역은 groupTheme 토큰(gt-primary) 경유가 원칙이라 랜딩(SplashContactPage, shell.point) 과는
 * 색 계열이 다르다.
 *
 * 정형 시나리오 Q&A는 sv-chat(indiChat) 실 API(POST /api/chat/sessions·/api/chat/messages)로
 * 동작한다 — idpAdmin "챗봇 시나리오 관리"에서 등록한 노드가 그대로 매칭된다.
 * "상담원과 대화하기"는 sv-chat의 /handoff가 내부적으로 sv-support에 case를 생성해 caseId를
 * 돌려주고(C-03 §9 서비스 간 직접 호출), 그 caseId로 sv-support와 WS(STOMP/SockJS) 직접 연결해
 * 실시간 채팅한다 — 목업 없음. 진입 시 진행중인 case가 있으면(getMyActiveCase) 봇 세션을 새로
 * 열지 않고 바로 그 case로 이어서 들어간다("이어보기").
 * 대화 흐름을 하나의 시간순 events 배열로 관리해 챗봇 Q&A·연결 배너·상담원 응답이 클릭한
 * 순서대로 섞여 쌓이도록 한다(핸드오프 블록만 하단에 고정되는 문제 방지).
 */
import { useEffect, useRef, useState } from 'react';
import { useAuthStore } from '@indipia/shared';
import { Avatar, Button, Input, Modal } from '@indipia/ui-core';
import {
  isHandoffFollowup, sendChatHandoff, sendChatMessage, startChatSession,
  type ChatFollowup, type ChatMessageInput,
} from '../api/chat';
import { closeSupportCase, getMyActiveCase, listSupportMessages, markSupportRead, type SupportCaseCreateDto, type SupportMessageDto } from '../api/supportChat';
import {
  connectSupportChat, disconnectSupportChat, sendSupportChatMessage, subscribeSupportChatTopic,
} from '../realtime/supportChatRealtime';

type CaseStatus = SupportCaseCreateDto['caseStatus'];

interface RealtimeEvent {
  type: string;
  [key: string]: unknown;
}

function safeParse<T>(body: string): T | null {
  try {
    return JSON.parse(body) as T;
  } catch {
    return null;
  }
}

interface QaEvent {
  id: number;
  kind: 'qa';
  responder: 'bot';
  question: string;
  answer: string | null; // null = 답변 대기(타이핑 중)
}

interface HandoffEvent {
  id: number;
  kind: 'handoff';
  stage: 'connecting' | 'connected';
}

interface NoteEvent {
  id: number;
  kind: 'note';
  text: string;
}

interface LiveMessageEvent {
  id: number;
  kind: 'liveMsg';
  from: 'CUSTOMER' | 'AGENT';
  text: string;
  messageId: number;
}

type ChatEvent = QaEvent | HandoffEvent | NoteEvent | LiveMessageEvent;

function BotIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="3" y="11" width="18" height="10" rx="2" />
      <circle cx="12" cy="5" r="2" />
      <path d="M12 7v4" />
      <line x1="8" y1="16" x2="8" y2="16" />
      <line x1="16" y1="16" x2="16" y2="16" />
    </svg>
  );
}

function SendIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <line x1="22" y1="2" x2="11" y2="13" />
      <polygon points="22 2 15 22 11 13 2 9 22 2" />
    </svg>
  );
}

function BotAvatar({ small }: { small?: boolean }) {
  return (
    <Avatar aria-hidden className={small ? '!h-7 !w-7 shrink-0' : undefined}>
      <span className={small ? 'scale-75' : undefined}><BotIcon /></span>
    </Avatar>
  );
}

function AgentAvatar() {
  return (
    <span
      className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[10px] font-bold"
      style={{ background: 'var(--gt-primary)', color: 'var(--gt-on-primary)' }}
      aria-hidden
    >
      상담
    </span>
  );
}

function TypingDots() {
  return (
    <span
      className="inline-flex items-center gap-1 rounded-2xl rounded-tl-sm border px-3.5 py-3"
      style={{ background: 'var(--shell-surface)', borderColor: 'var(--shell-border)' }}
    >
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="h-1.5 w-1.5 animate-bounce rounded-full"
          style={{ background: 'var(--shell-hint)', animationDelay: `${i * 0.15}s` }}
        />
      ))}
    </span>
  );
}

const botBubbleStyle = { background: 'var(--shell-surface)', borderColor: 'var(--shell-border)' };
const agentBubbleStyle = {
  background: 'color-mix(in srgb, var(--gt-primary) 6%, var(--shell-surface))',
  borderColor: 'color-mix(in srgb, var(--gt-primary) 22%, transparent)',
};

export function StudioChatbotPage() {
  const memberName = useAuthStore((s) => s.user)?.memberName?.trim() || '사장님';

  const seqRef = useRef(0);
  const nextId = () => (seqRef.current += 1);

  const [events, setEvents] = useState<ChatEvent[]>([]);
  const [liveMode, setLiveMode] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const threadRef = useRef<HTMLDivElement>(null);

  const [sessionId, setSessionId] = useState<string | null>(null);
  const [welcomeAnswer, setWelcomeAnswer] = useState<string | null>(null);
  const [followups, setFollowups] = useState<ChatFollowup[]>([]);
  const rootsRef = useRef<ChatFollowup[]>([]);

  const [caseId, setCaseId] = useState<string | null>(null);
  const [caseStatus, setCaseStatus] = useState<CaseStatus | null>(null);
  const [confirmEndOpen, setConfirmEndOpen] = useState(false);
  const [ending, setEnding] = useState(false);

  const substituteMemberName = (text: string) => text.replace('{member_name}', memberName);

  function beginBotSession() {
    startChatSession()
      .then((res) => {
        setSessionId(res.sessionId);
        setWelcomeAnswer(substituteMemberName(res.welcome.answer));
        rootsRef.current = res.roots;
        setFollowups(res.roots);
      })
      .catch(() => {
        setWelcomeAnswer(`안녕하세요, ${memberName}님! 저는 인디봇이에요.\n사용 중 궁금한 점을 편하게 물어보세요.`);
      });
  }

  // 화면을 벗어나면 실시간 연결을 정리한다.
  useEffect(() => disconnectSupportChat, []);

  // 이어보기 — 진입 시 진행중인 case가 있으면 봇 세션 없이 바로 그 case로 들어간다.
  useEffect(() => {
    getMyActiveCase()
      .then((res) => {
        const active = res.data.data;
        if (active) {
          setLiveMode(true);
          setCaseStatus(active.caseStatus);
          setEvents((prev) => [...prev, { id: nextId(), kind: 'handoff', stage: active.caseStatus === 'IN_PROGRESS' ? 'connected' : 'connecting' }]);
          setCaseId(String(active.caseId));
        } else {
          beginBotSession();
        }
      })
      .catch(() => beginBotSession());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 실시간 case 연결 — WS 접속 + 이력 로드 + 구독.
  useEffect(() => {
    if (!caseId) return;

    connectSupportChat();

    listSupportMessages(caseId)
      .then((res) => {
        const items = res.data.data.items;
        setEvents((prev) => [
          ...prev,
          ...items.map((m: SupportMessageDto): LiveMessageEvent => ({
            id: nextId(), kind: 'liveMsg', from: m.senderType as 'CUSTOMER' | 'AGENT', text: m.messageContent, messageId: m.messageId,
          })),
        ]);
        // 이 화면이 열려있는 동안 이미 다 보인 메시지들 — 여기까지 읽음 처리(비대칭 읽음, FR-13).
        if (items.length > 0) {
          const lastId = Math.max(...items.map((m) => m.messageId));
          markSupportRead(caseId, lastId).catch(() => {});
        }
      })
      .catch(() => {});

    return subscribeSupportChatTopic(`/topic/support.case.${caseId}`, (body) => {
      const event = safeParse<RealtimeEvent>(body);
      if (!event) return;

      if (event.type === 'MESSAGE') {
        const messageId = Number(event.messageId);
        setEvents((prev) => (
          prev.some((e) => e.kind === 'liveMsg' && e.messageId === messageId)
            ? prev
            : [...prev, {
                id: nextId(), kind: 'liveMsg',
                from: event.senderType as 'CUSTOMER' | 'AGENT',
                text: String(event.content), messageId,
              }]
        ));
        // 이 화면이 켜져 있는 한 새 메시지는 바로 보이므로(자동 스크롤), 도착 즉시 읽음 처리한다.
        markSupportRead(caseId, messageId).catch(() => {});
      } else if (event.type === 'STATUS_CHANGED') {
        const status = event.status as CaseStatus;
        setCaseStatus(status);
        setEvents((prev) => prev.map((e) => (
          e.kind === 'handoff' && status === 'IN_PROGRESS' ? { ...e, stage: 'connected' } : e
        )));
        if (status === 'DONE') {
          setEvents((prev) => [...prev, { id: nextId(), kind: 'note', text: '상담이 종료되었습니다' }]);
        }
      }
    });
  }, [caseId]);

  useEffect(() => {
    threadRef.current?.scrollTo({ top: threadRef.current.scrollHeight, behavior: 'smooth' });
  }, [events]);

  async function askBot(input: ChatMessageInput, questionLabel: string) {
    if (!sessionId) return;
    const id = nextId();
    setEvents((prev) => [...prev, { id, kind: 'qa', responder: 'bot', question: questionLabel, answer: null }]);
    try {
      const result = await sendChatMessage(sessionId, input);
      const answerText = substituteMemberName(result.answer ?? result.prompt ?? '');
      setEvents((prev) => prev.map((e) => (e.kind === 'qa' && e.id === id ? { ...e, answer: answerText } : e)));
      setFollowups(result.followups ?? result.candidates ?? rootsRef.current);
    } catch {
      setEvents((prev) => prev.map((e) => (
        e.kind === 'qa' && e.id === id ? { ...e, answer: '일시적으로 답변이 어려워요. 다시 시도해 주세요.' } : e
      )));
    }
  }

  function onFollowupClick(f: ChatFollowup) {
    if (isHandoffFollowup(f.nodeCode)) { void openHandoff(); return; }
    if (f.nodeCode === '@ROOTS') { setFollowups(rootsRef.current); return; }
    void askBot({ type: 'QUICK_REPLY', nodeCode: f.nodeCode }, f.quickReply ?? f.nodeCode);
  }

  async function openHandoff() {
    if (liveMode || !sessionId) return;
    const id = nextId();
    setLiveMode(true);
    setEvents((prev) => [...prev, { id, kind: 'handoff', stage: 'connecting' }]);
    // memberName은 FlowGate 주입 헤더에 없어 indiChat이 조회할 방법이 없으므로 로그인 세션 값을 실어 보낸다.
    const rawMemberName = useAuthStore.getState().user?.memberName?.trim() || undefined;
    try {
      const hint = await sendChatHandoff(sessionId, { memberName: rawMemberName });
      if (hint.nextStep !== 'REALTIME' || !hint.caseId) {
        setEvents((prev) => [
          ...prev.filter((e) => !(e.kind === 'handoff' && e.id === id)),
          { id: nextId(), kind: 'note', text: '지금은 실시간 상담 연결이 어려워요. 잠시 후 다시 시도해 주세요.' },
        ]);
        setLiveMode(false);
        return;
      }
      setCaseId(hint.caseId);
      setCaseStatus('WAITING');
    } catch {
      setEvents((prev) => [
        ...prev.filter((e) => !(e.kind === 'handoff' && e.id === id)),
        { id: nextId(), kind: 'note', text: '상담사 연결에 실패했어요. 잠시 후 다시 시도해 주세요.' },
      ]);
      setLiveMode(false);
    }
  }

  function handleSend() {
    const text = inputValue.trim();
    if (!text) return;
    if (liveMode) {
      if (!caseId || caseStatus !== 'IN_PROGRESS') return;
      if (!sendSupportChatMessage(caseId, text)) {
        setEvents((prev) => [...prev, { id: nextId(), kind: 'note', text: '연결이 아직 준비되지 않았어요. 잠시 후 다시 시도해 주세요.' }]);
        return;
      }
      setInputValue('');
    } else {
      setInputValue('');
      void askBot({ type: 'FREE_TEXT', text }, text);
    }
  }

  async function handleEndChat() {
    if (!caseId || ending) return;
    setEnding(true);
    try {
      await closeSupportCase(caseId);
      setConfirmEndOpen(false);
      // 상태 반영 자체는 STATUS_CHANGED(WS) 브로드캐스트로 자기 자신에게도 돌아온다(에코 패턴, 메시지와 동일).
    } catch {
      setEvents((prev) => [...prev, { id: nextId(), kind: 'note', text: '상담 종료에 실패했어요. 잠시 후 다시 시도해 주세요.' }]);
    } finally {
      setEnding(false);
    }
  }

  const canChat = caseStatus === 'IN_PROGRESS';
  const isDone = caseStatus === 'DONE';

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="mx-auto max-w-3xl px-20 py-8 pb-16">

        {/* 헤더 */}
        <div className="mb-6">
          <p className="mb-1.5 text-xs font-bold uppercase tracking-widest" style={{ color: 'var(--gt-primary)' }}>
            고객센터
          </p>
          <h1 className="mb-1 text-xl font-extrabold tracking-tight text-text">챗봇상담</h1>
          <p className="text-xs text-text-muted">인디봇에게 편하게 물어보세요. 더 도움이 필요하면 상담원과 연결해드려요</p>
        </div>

        {/* 챗봇 위젯 */}
        <div className="overflow-hidden rounded-xl border" style={{ background: 'var(--shell-surface)', borderColor: 'var(--shell-border)' }}>

          {/* 위젯 헤더 */}
          <div className="flex items-center gap-3 border-b px-5 py-4" style={{ borderColor: 'var(--shell-border)' }}>
            <BotAvatar />
            <div className="min-w-0 flex-1">
              <p className="flex items-center gap-1.5 text-sm font-bold text-text">
                인디봇
                <span className="inline-block h-1.5 w-1.5 rounded-full" style={{ background: 'var(--color-success)' }} aria-hidden />
              </p>
              <p className="text-xs text-text-muted">AI 챗봇이 답변해요 · 평균 3초 이내 응답</p>
            </div>
            {liveMode && !isDone && (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => setConfirmEndOpen(true)}
                className="!shrink-0 !rounded-full !px-3.5 !py-1.5 !text-xs !font-semibold"
              >
                상담 종료
              </Button>
            )}
          </div>

          {/* 대화 영역 */}
          <div ref={threadRef} className="flex max-h-[480px] flex-col gap-3 overflow-y-auto px-5 py-5" style={{ background: 'var(--shell-bg)' }}>

            {/* 첫 인사 — 세션 개시 응답의 웰컴 노드(sv-chat). 이어보기로 들어온 경우엔 봇 세션 자체가 없어 생략 */}
            {!liveMode && (
              <div className="flex items-start gap-2.5">
                <BotAvatar small />
                {welcomeAnswer === null ? (
                  <TypingDots />
                ) : (
                  <div
                    className="max-w-[85%] whitespace-pre-line rounded-2xl rounded-tl-sm border px-3.5 py-2.5 text-sm leading-relaxed text-text"
                    style={botBubbleStyle}
                  >
                    {welcomeAnswer}
                  </div>
                )}
              </div>
            )}

            {/* 대화 이벤트 — 클릭한 순서 그대로 렌더링 */}
            {events.map((event) => {
              if (event.kind === 'note') {
                return (
                  <p key={event.id} className="text-center text-xs text-text-muted">{event.text}</p>
                );
              }

              if (event.kind === 'handoff') {
                if (event.stage === 'connecting') {
                  return (
                    <div key={event.id} className="flex w-full flex-col items-center gap-2">
                      <p className="text-center text-xs text-text-muted">
                        상담사와 연결 중이에요 (지금까지의 대화 내용이 함께 전달됩니다)
                      </p>
                      <div className="flex w-full items-start gap-2.5">
                        <AgentAvatar />
                        <TypingDots />
                      </div>
                    </div>
                  );
                }
                return (
                  <p key={event.id} className="text-center text-xs text-text-muted">
                    상담사가 연결되었어요 — 편하게 이야기해 주세요
                  </p>
                );
              }

              if (event.kind === 'liveMsg') {
                const isMine = event.from === 'CUSTOMER';
                if (isMine) {
                  return (
                    <div key={event.id} className="flex justify-end">
                      <div
                        className="max-w-[85%] rounded-2xl rounded-tr-sm px-3.5 py-2.5 text-sm font-medium"
                        style={{ background: 'var(--gt-primary)', color: 'var(--gt-on-primary)' }}
                      >
                        {event.text}
                      </div>
                    </div>
                  );
                }
                return (
                  <div key={event.id} className="flex items-start gap-2.5">
                    <AgentAvatar />
                    <div
                      className="max-w-[85%] whitespace-pre-line rounded-2xl rounded-tl-sm border px-3.5 py-2.5 text-sm leading-relaxed text-text"
                      style={agentBubbleStyle}
                    >
                      {event.text}
                    </div>
                  </div>
                );
              }

              // event.kind === 'qa'
              return (
                <div key={event.id} className="flex flex-col gap-3">
                  <div className="flex justify-end">
                    <div
                      className="max-w-[85%] rounded-2xl rounded-tr-sm px-3.5 py-2.5 text-sm font-medium"
                      style={{ background: 'var(--gt-primary)', color: 'var(--gt-on-primary)' }}
                    >
                      {event.question}
                    </div>
                  </div>
                  <div className="flex items-start gap-2.5">
                    <BotAvatar small />
                    {event.answer === null ? (
                      <TypingDots />
                    ) : (
                      <div
                        className="max-w-[85%] whitespace-pre-line rounded-2xl rounded-tl-sm border px-3.5 py-2.5 text-sm leading-relaxed text-text"
                        style={botBubbleStyle}
                      >
                        {event.answer}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}

            {/* 퀵리플라이 — 실시간 상담 연결 후에는 봇 퀵리플라이를 숨긴다 */}
            {!liveMode && (
              <div className="flex flex-wrap gap-2 pl-9">
                {followups.map((f, idx) => (
                  <Button
                    key={`${f.nodeCode}-${idx}`}
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => onFollowupClick(f)}
                    className="!rounded-full !px-3.5 !py-1.5 !text-xs !font-semibold"
                  >
                    {f.quickReply ?? f.nodeCode}
                  </Button>
                ))}
              </div>
            )}
          </div>

          {/* 입력창 */}
          <div className="flex items-center gap-2 border-t px-4 py-3" style={{ borderColor: 'var(--shell-border)', background: 'var(--shell-surface)' }}>
            <Input
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSend(); }}
              placeholder={
                liveMode
                  ? (isDone ? '상담이 종료되었습니다' : canChat ? '상담사에게 보낼 메시지를 입력해 주세요' : '상담사와 연결 중이에요…')
                  : '궁금한 점을 입력해 주세요'
              }
              disabled={liveMode ? !canChat : !sessionId}
              size="sm"
              width="fill"
              aria-label="챗봇에게 질문 입력"
            />
            <Button
              type="button" variant="primary" size="sm" onClick={handleSend} aria-label="전송"
              disabled={liveMode ? !canChat : !sessionId}
              className="!h-9 !w-9 !rounded-full !p-0"
            >
              <SendIcon />
            </Button>
          </div>
        </div>
      </div>

      <Modal open={confirmEndOpen} onClose={() => setConfirmEndOpen(false)} title="상담을 종료할까요?">
        <p className="mb-4 text-sm text-text-muted">종료하면 이 상담은 다시 이어갈 수 없어요.</p>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" size="sm" onClick={() => setConfirmEndOpen(false)}>
            취소
          </Button>
          <Button type="button" variant="primary" size="sm" onClick={() => void handleEndChat()} disabled={ending}>
            {ending ? '종료 중…' : '종료하기'}
          </Button>
        </div>
      </Modal>
    </div>
  );
}
